import { useMemo } from 'react';

// Game color palette for avatar generation
const AVATAR_COLORS = [
  '#22c55e', // green (success)
  '#3b82f6', // blue
  '#a855f7', // purple
  '#fbbf24', // gold
  '#00f0ff', // cyan
  '#ef4444', // red
  '#f59e0b', // orange
  '#ec4899', // pink
];

// Background is always dark to match the theme
const BG_COLOR = 'transparent';

interface WalletAvatarProps {
  address: string;
  size?: number;
  className?: string;
}

// Simple hash function for deterministic randomness
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

// Get a seeded random number from hash
function seededRandom(seed: number, index: number): number {
  const x = Math.sin(seed + index * 9999) * 10000;
  return x - Math.floor(x);
}

export function WalletAvatar({ address, size = 48, className = '' }: WalletAvatarProps) {
  const { grid } = useMemo(() => {
    const hash = hashCode(address);

    // Pick 2 colors for this avatar (foreground variations)
    const colorIndex1 = hash % AVATAR_COLORS.length;
    const colorIndex2 = (hash >> 4) % AVATAR_COLORS.length;
    const primaryColor = AVATAR_COLORS[colorIndex1]!;
    const secondaryColor = colorIndex1 === colorIndex2
      ? AVATAR_COLORS[(colorIndex2 + 1) % AVATAR_COLORS.length]!
      : AVATAR_COLORS[colorIndex2]!;

    // Generate 5x5 grid (only need to generate 3 columns, then mirror)
    const grid: (string | null)[][] = [];

    for (let row = 0; row < 5; row++) {
      const rowData: (string | null)[] = [];
      for (let col = 0; col < 3; col++) {
        const index = row * 3 + col;
        const rand = seededRandom(hash, index);

        if (rand < 0.35) {
          // Empty cell
          rowData.push(null);
        } else if (rand < 0.7) {
          // Primary color
          rowData.push(primaryColor);
        } else {
          // Secondary color
          rowData.push(secondaryColor);
        }
      }

      // Mirror to create symmetry (5 columns total)
      const mirroredRow = [...rowData, rowData[1]!, rowData[0]!];
      grid.push(mirroredRow);
    }

    return { grid, colors: [primaryColor, secondaryColor] };
  }, [address]);

  const cellSize = size / 5;

  return (
    <div
      className={`rounded-full overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: BG_COLOR,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(5, ${cellSize}px)`,
          gridTemplateRows: `repeat(5, ${cellSize}px)`,
          width: size,
          height: size,
        }}
      >
        {grid.map((row, rowIndex) =>
          row.map((color, colIndex) => (
            <div
              key={`${rowIndex}-${colIndex}`}
              style={{
                backgroundColor: color || 'transparent',
                width: cellSize,
                height: cellSize,
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Smaller variant optimized for table rows
export function WalletAvatarSmall({ address, className = '' }: { address: string; className?: string }) {
  return <WalletAvatar address={address} size={32} className={className} />;
}
